import { useState } from "react";
import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";

import UserInfo from "src/components/common/userInfo";
import { getFileNameFromUrl, testerEmails } from "src/components/common/functions";
import { useAuthStore, User } from "../main/authStore";

import { Purchase } from "./types";

export default function PurchaseDetail() {
    const { purchase } = useRouteLoaderData("Purchase") as { purchase: Purchase };
    const user = useAuthStore(state => state.user) as User;
    const submit = useSubmit();
    const [comments, setComments] = useState(purchase.approverNotes ?? "");
    const isFinal = ["Approved", "Rejected"].includes(purchase.status);
    const canEdit = user.purchases === "edit" && purchase.void === "no" && !isFinal;
    const canApprove = purchase.approverId === user.id && purchase.void === "no" && !isFinal;

    const submitSimple = (action: string, values: Record<string, string>) => {
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => formData.append(key, value));
        submit(formData, { method: "post", action });
    };

    const handleDownload = async (url: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(url) ?? "download.pdf");
        } catch (error) {
            window.alert("Error downloading file:" + error);
        }
    };

    return (
        <div className = "border" style = {{ borderRadius: "6px" }}>
            <div className = "cssSeperateWhenWide p-3">
                <table className = "table table-responsive-xl noTopBorder flex-grow-1 me-3">
                    <tbody>
                        <tr>
                            <th>Status</th>
                            <td><span className = "badge text-bg-primary">{purchase.status}</span></td>
                        </tr>
                        <tr>
                            <th>PO Number</th>
                            <td>{purchase.poNumber}</td>
                        </tr>
                        <tr>
                            <th>PO Date</th>
                            <td>{purchase.poDate}</td>
                        </tr>
                        <tr>
                            <th>PO Type</th>
                            <td>{purchase.poType === "re" ? "Reimbursement" : "Purchase Request"}</td>
                        </tr>
                        <tr>
                            <th>Category</th>
                            <td>{purchase.category}</td>
                        </tr>
                        <tr>
                            <th>Requester</th>
                            <td><UserInfo name = {purchase.requesterName} id = {purchase.requesterId} /></td>
                        </tr>
                        <tr>
                            <th>Department</th>
                            <td>{purchase.department}</td>
                        </tr>
                        {purchase.projectId ? (
                            <tr>
                                <th>Project</th>
                                <td><Link to = {`/Projects/${purchase.projectId}`}>{purchase.projectName}</Link></td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Approver</th>
                            <td><UserInfo name = {purchase.approverName} id = {purchase.approverId} /></td>
                        </tr>
                        <tr>
                            <th>Payment Method</th>
                            <td>{purchase.paymentMethod}</td>
                        </tr>
                        {purchase.last4 ? (
                            <tr>
                                <th>Last 4</th>
                                <td>{purchase.last4}</td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Billable</th>
                            <td>{purchase.billable}</td>
                        </tr>
                        <tr>
                            <th>Included In Proposal</th>
                            <td>{purchase.includedInProposal}</td>
                        </tr>
                        {purchase.clientInvoiceNumber ? (
                            <tr>
                                <th>Client Invoice Number</th>
                                <td>{purchase.clientInvoiceNumber}</td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Notes</th>
                            <td><span style = {{ whiteSpace: "pre-wrap" }}>{purchase.notes}</span></td>
                        </tr>
                    </tbody>
                </table>
                <table className = "table table-responsive-xl noTopBorder flex-grow-1">
                    <tbody>
                        <tr>
                            <th>Subtotal</th>
                            <td>${purchase.subtotal}</td>
                        </tr>
                        <tr>
                            <th>Tax</th>
                            <td>${purchase.tax}</td>
                        </tr>
                        <tr>
                            <th>Discount</th>
                            <td>${purchase.discount}</td>
                        </tr>
                        <tr>
                            <th>Total</th>
                            <td>${purchase.total}</td>
                        </tr>
                        <tr>
                            <th>Paid</th>
                            <td>{purchase.paid}</td>
                        </tr>
                        <tr>
                            <th>Billed</th>
                            <td>{purchase.billed}</td>
                        </tr>
                        <tr>
                            <th>Submitted By</th>
                            <td>
                                <UserInfo name = {purchase.submitterName} id = {purchase.submitterId} />
                                <span className = "ms-2">{purchase.submitTime}</span>
                            </td>
                        </tr>
                        <tr>
                            <th>Approval Time</th>
                            <td>{purchase.approvalTime}</td>
                        </tr>
                        <tr>
                            <th>Approver Notes</th>
                            <td><span style = {{ whiteSpace: "pre-wrap" }}>{purchase.approverNotes}</span></td>
                        </tr>
                        {purchase.notifiedAt ? (
                            <tr>
                                <th>Notified By</th>
                                <td>
                                    <UserInfo name = {purchase.notifierName} id = {purchase.notifiedBy} />
                                    <span className = "ms-2">{purchase.notifiedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Creator</th>
                            <td>
                                <UserInfo name = {purchase.creatorName} id = {purchase.creatorId} />
                                <span className = "ms-2">{purchase.createdAt}</span>
                            </td>
                        </tr>
                        {purchase.updaterId ? (
                            <tr>
                                <th>Updater</th>
                                <td>
                                    <UserInfo name = {purchase.updaterName} id = {purchase.updaterId} />
                                    <span className = "ms-2">{purchase.updatedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        {purchase.void === "yes" ? (
                            <>
                                <tr>
                                    <th className = "text-danger">Void Reason</th>
                                    <td>{purchase.voidReason}</td>
                                </tr>
                                <tr>
                                    <th>Validate Reason</th>
                                    <td>{purchase.validateReason}</td>
                                </tr>
                            </>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <div className = "table-responsive px-3">
                <table className = "table">
                    <thead className = "noTableTopBorder">
                        <tr>
                            <th>Vendor</th>
                            <th>Description</th>
                            <th>Unit Price</th>
                            <th>Quantity</th>
                            <th>Line Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {purchase.data.map((line, index) => (
                            <tr key = {index}>
                                <td>{line.vendorId ? <Link to = {`/Vendors/${line.vendorId}`}>{line.vendorName}</Link> : line.vendorName}</td>
                                <td>{line.description}</td>
                                <td>${line.unitPrice}</td>
                                <td>{line.quantity}</td>
                                <td>${line.lineTotal}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className = "mb-3 px-3">
                {canEdit ? <Link className = "btn btn-outline-primary" to = "Edit">Edit</Link> : null}
                {purchase.void === "no" ? (
                    <button
                        type = "button"
                        className = "btn btn-danger ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to void this purchase form?")) return;
                            let voidReason = window.prompt("reason to void:");
                            if (voidReason === null) return;
                            while (voidReason === "") {
                                window.alert("Reason is required to void.");
                                voidReason = window.prompt("reason to void:");
                                if (voidReason === null) return;
                            }
                            submitSimple("Void", { id: String(purchase.id), voidReason });
                        }}
                    >
                        Void
                    </button>
                ) : (
                    <>
                        {testerEmails.includes(user.email) ? (
                            <button
                                type = "button"
                                className = "btn btn-primary ms-2"
                                onClick = {() => {
                                    if (!window.confirm("Are you sure to validate this purchase form?")) return;
                                    let validateReason = window.prompt("reason to validate:");
                                    if (validateReason === null) return;
                                    while (validateReason === "") {
                                        window.alert("Reason is required to validate.");
                                        validateReason = window.prompt("reason to validate:");
                                        if (validateReason === null) return;
                                    }
                                    submitSimple("Validate", { id: String(purchase.id), validateReason });
                                }}
                            >
                                Validate
                            </button>
                        ) : null}
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to delete this purchase form?\n\nThis action cannot be undone.")) return;
                                submitSimple("Delete", { id: String(purchase.id) });
                            }}
                        >
                            Delete
                        </button>
                    </>
                )}
                {purchase.void === "no" && !isFinal && purchase.approverId !== user.id ? (
                    <button
                        type = "button"
                        className = "btn btn-info ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to notify the approver?")) return;
                            submitSimple("Notify", { id: String(purchase.id) });
                        }}
                    >
                        Notify
                    </button>
                ) : null}
                {user.purchases === "paid" ? (
                    <button
                        type = "button"
                        className = "btn btn-primary ms-2"
                        onClick = {() => submitSimple("Paid", { id: String(purchase.id), paid: purchase.paid === "yes" ? "no" : "yes" })}
                    >
                        {purchase.paid === "yes" ? "Unmark Paid" : "Mark Paid"}
                    </button>
                ) : null}
                {purchase.quoteUrl ? (
                    <button type = "button" className = "btn btn-outline-primary ms-2" onClick = {() => handleDownload(purchase.quoteUrl)}>
                        Quote
                    </button>
                ) : null}
                {purchase.receiptUrl ? (
                    <button type = "button" className = "btn btn-outline-primary ms-2" onClick = {() => handleDownload(purchase.receiptUrl)}>
                        Receipt
                    </button>
                ) : null}
                {purchase.pdfId ? (
                    <button type = "button" className = "btn btn-primary ms-2" onClick = {() => handleDownload(purchase.pdfId)}>
                        PDF
                    </button>
                ) : null}
                <Link className = "btn btn-outline-secondary float-end" to = ".." relative = "path">Back</Link>
            </div>
            {canApprove ? (
                <div className = "px-3 pb-3">
                    <label className = "fw-bold mb-0" htmlFor = "approverNotes">Approver Comments:</label>
                    <textarea
                        id = "approverNotes"
                        className = "form-control"
                        rows = {4}
                        value = {comments}
                        onChange = {e => setComments(e.currentTarget.value)}
                    />
                    <div className = "mt-3">
                        <button
                            type = "button"
                            className = "btn btn-success"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to approve this purchase form?")) return;
                                submitSimple("Approve", {
                                    id: String(purchase.id),
                                    approverDecision: "Approved",
                                    approverNotes: comments,
                                });
                            }}
                        >
                            Approve
                        </button>
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to reject this purchase form?")) return;
                                submitSimple("Approve", {
                                    id: String(purchase.id),
                                    approverDecision: "Rejected",
                                    approverNotes: comments,
                                });
                            }}
                        >
                            Reject
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
